import { useEffect, useMemo, useState } from "react";

import { offlineDb } from "@/lib/offline/db";

/** The registers a check form lists its units from. Also the cache key prefix. */
export type OfflineRegister =
  | "emergency-lights"
  | "fire-alarm-call-points"
  | "fire-doors";

/** The parts of a generated register list query this hook reads. */
interface RegisterQuery<T> {
  data?: { data: T[] };
  dataUpdatedAt: number;
  isLoading: boolean;
}

interface OfflineRegisterState<T> {
  items: T[];
  isLoading: boolean;
  /** Rendering the cached copy because no fetch has succeeded this mount. */
  isServedFromCache: boolean;
}

/**
 * Offline fallback for a register list: hydrates the last copy from IndexedDB
 * and writes every successful fetch back, so a cold start with no signal still
 * lists the units instead of claiming none are registered. Online, the caller's
 * own query still wins.
 */
export function useOfflineRegister<T>(
  register: OfflineRegister,
  nurseryId: string | null,
  query: RegisterQuery<T>,
): OfflineRegisterState<T> {
  const cacheKey = nurseryId ? `${register}-${nurseryId}` : null;
  const [cached, setCached] = useState<{ key: string; items: T[] } | null>(
    null,
  );
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!cacheKey) return;
    let cancelled = false;
    (async () => {
      const row = await offlineDb.registerCache.get(cacheKey);
      if (cancelled) return;
      setCached(row ? { key: cacheKey, items: row.items as T[] } : null);
      setLoadedKey(cacheKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  const serverItems = query.data?.data;
  const fetchedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (!cacheKey) return;
    if (!nurseryId) return;
    if (!serverItems) return;
    void offlineDb.registerCache.put({
      id: cacheKey,
      nurseryId,
      items: serverItems,
      cachedAt: fetchedAt,
    });
  }, [cacheKey, nurseryId, serverItems, fetchedAt]);

  // Keyed so a nursery switch never shows the previous nursery's register.
  const cachedItems = cached?.key === cacheKey ? cached?.items : undefined;
  const isCacheLoading = !!cacheKey && loadedKey !== cacheKey;

  const items = useMemo(
    () => serverItems ?? cachedItems ?? [],
    [serverItems, cachedItems],
  );

  return {
    items,
    isLoading: isCacheLoading || query.isLoading,
    isServedFromCache: !serverItems && !!cachedItems,
  };
}
